import { StateObservable } from 'redux-observable';
import { Observable } from 'rxjs';
import { catchError, filter, ignoreElements, map, mergeMap } from 'rxjs/operators';

import { IExternalEducationTemplate } from '@src/model/externalEducationTemplate/ExternalEducationTemplate';
import EntityApiServiceRegistry from '@src/service/api/registry/entity/EntityApiServiceRegistry';
import ListFilterBusinessStore from '@src/service/business/common/listFilterBusinessStore';
import { ICollectionData, ICollectionFetchPayload, ILemonAction, IPayloadAction } from '@src/service/business/common/types';
import { createStaticMessageUserFeedbackError } from '@src/service/business/common/userFeedbackUtils';
import { actionThunk } from '@src/service/util/observable/operators';
import { startGlobalProgress, stopGlobalProgress } from '@src/service/util/observable/operators';
import { reportCaughtMessage } from '@src/service/util/observable/operators/userFeedback';

// -
// -------------------- Types&Consts

export interface IExternalEducationTemplateListFilter {
  educationProvider?: string;
  title?: string;
  skills?: number[];
  educationSector?: number;
  minDuration?: number;
  maxDuration?: number;
  enrollmentRequirements?: number[];
}

// List filter ID
const EXTERNAL_EDUCATION_TEMPLATE_LIST_FILTER = '@@EXTERNAL_EDUCATION_TEMPLATE_LIST_FILTER';

// -
// -------------------- Selectors

const getExternalEducationTemplateList = (store: any): ICollectionData<IExternalEducationTemplate> => store.externalEducationTemplateList;
const getExternalEducationTemplateListFilter = (store: any): IExternalEducationTemplateListFilter => ListFilterBusinessStore.selectors.getListFilter(store, EXTERNAL_EDUCATION_TEMPLATE_LIST_FILTER);

// -
// -------------------- Actions

const Actions = {
  EXTERNAL_EDUCATION_TEMPLATE_LIST_FETCH: 'EXTERNAL_EDUCATION_TEMPLATE_LIST_FETCH',
  EXTERNAL_EDUCATION_TEMPLATE_PICKER_LIST_FETCH: 'EXTERNAL_EDUCATION_TEMPLATE_PICKER_LIST_FETCH',
  EXTERNAL_EDUCATION_TEMPLATE_LIST_LOAD: 'EXTERNAL_EDUCATION_TEMPLATE_LIST_LOAD',
  EXTERNAL_EDUCATION_TEMPLATE_LIST_CLEAR: 'EXTERNAL_EDUCATION_TEMPLATE_LIST_CLEAR',
};

const fetchExternalEducationTemplateList = (payload: ICollectionFetchPayload<IExternalEducationTemplateListFilter>): IPayloadAction<ICollectionFetchPayload<IExternalEducationTemplateListFilter>> => {
  return {
    type: Actions.EXTERNAL_EDUCATION_TEMPLATE_LIST_FETCH,
    payload,
  };
};

const fetchExternalEducationTemplatePickerList = (externalEducationFilter: IExternalEducationTemplateListFilter): IPayloadAction<IExternalEducationTemplateListFilter> => {
  return {
    type: Actions.EXTERNAL_EDUCATION_TEMPLATE_PICKER_LIST_FETCH,
    payload: externalEducationFilter,
  };
};

const loadExternalEducationTemplateList = (data: ICollectionData<IExternalEducationTemplate>): IPayloadAction<ICollectionData<IExternalEducationTemplate>> => {
  return {
    type: Actions.EXTERNAL_EDUCATION_TEMPLATE_LIST_LOAD,
    payload: data,
  };
};

const clearExternalEducationTemplateList = (): ILemonAction => {
  return {
    type: Actions.EXTERNAL_EDUCATION_TEMPLATE_LIST_CLEAR,
  };
};

const storeExternalEducationTemplateListFilter = (externalEducationFilter: IExternalEducationTemplateListFilter): ILemonAction => {
  return ListFilterBusinessStore.actions.storeListFilter(EXTERNAL_EDUCATION_TEMPLATE_LIST_FILTER, externalEducationFilter);
};

const clearExternalEducationTemplateListFilter = (): ILemonAction => {
  return ListFilterBusinessStore.actions.clearListFilter(EXTERNAL_EDUCATION_TEMPLATE_LIST_FILTER);
};

// -
// -------------------- Side-effects

const fetchExternalEducationTemplateListEffect = (action$: Observable<IPayloadAction<ICollectionFetchPayload<IExternalEducationTemplateListFilter>>>, state$: StateObservable<any>) => {
  return action$.pipe(
    filter((action) => {
      return action.type === Actions.EXTERNAL_EDUCATION_TEMPLATE_LIST_FETCH;
    }),

    startGlobalProgress(),

    mergeMap((action) => {
      const payload = action.payload;

      return EntityApiServiceRegistry.getService('ExternalEducationTemplate')
        .fetchEntityList(payload)
        .pipe(actionThunk(action));
    }),

    stopGlobalProgress(),

    map((data) => {
      return loadExternalEducationTemplateList(data);
    }),

    reportCaughtMessage((error: any) => createStaticMessageUserFeedbackError('GENERAL_MESSAGE.GENERAL_FETCH_ERROR')),

    catchError((error: any, o: Observable<any>) => {
      console.error('Error fetching external education template list', error);
      return o;
    })
  );
};

const fetchExternalEducationTemplatePickerListEffect = (action$: Observable<IPayloadAction<IExternalEducationTemplateListFilter>>, state$: StateObservable<any>) => {
  return action$.pipe(
    filter((action) => {
      return action.type === Actions.EXTERNAL_EDUCATION_TEMPLATE_PICKER_LIST_FETCH;
    }),

    startGlobalProgress(),

    mergeMap((action) => {
      const payload = action.payload;

      return EntityApiServiceRegistry.getService('ExternalEducationTemplate')
        .fetchEntityList(payload)
        .pipe(actionThunk(action));
    }),

    stopGlobalProgress(),

    ignoreElements(),

    reportCaughtMessage((error: any) => createStaticMessageUserFeedbackError('GENERAL_MESSAGE.GENERAL_FETCH_ERROR')),

    catchError((error: any, o: Observable<any>) => {
      console.error('Error fetching external education template picker list', error);
      return o;
    })
  );
};

// -
// -------------------- Reducers

const externalEducationTemplateList = (state: ICollectionData<IExternalEducationTemplate> | null = null, action: IPayloadAction<ICollectionData<IExternalEducationTemplate>>) => {
  if (action.type === Actions.EXTERNAL_EDUCATION_TEMPLATE_LIST_LOAD) {
    return {
      ...action.payload,
    };
  } else if (action.type === Actions.EXTERNAL_EDUCATION_TEMPLATE_LIST_CLEAR) {
    return null;
  }

  return state;
};

// --
// -------------------- Business Store

export const ExternalEducationTemplateListBusinessStore = {
  actions: {
    fetchExternalEducationTemplateList,
    fetchExternalEducationTemplatePickerList,
    clearExternalEducationTemplateList,
    storeExternalEducationTemplateListFilter,
    clearExternalEducationTemplateListFilter,
  },

  selectors: {
    getExternalEducationTemplateList,
    getExternalEducationTemplateListFilter,
  },

  effects: {
    fetchExternalEducationTemplateListEffect,
    fetchExternalEducationTemplatePickerListEffect,
  },

  reducers: {
    externalEducationTemplateList,
  },
};

// --
// export business store
export default ExternalEducationTemplateListBusinessStore;
